import { Button } from '@repo/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { useAsyncData } from '@repo/ui/dataProvider'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Label } from '@repo/ui/label'
import { Circle, ShieldCheck } from 'lucide-react'
import { useMemo } from 'react'
import { Trans } from 'react-i18next'

import { storeActions } from '@/store/actions.ts'
import { useStoreDispatch } from '@/store/hooks'
import { emailSchema, userSchema } from '@/validation-schemas'

export function SignUpRoute() {
  const dispatch = useStoreDispatch()

  const { t, tc } = useT({ keyPrefix: 'signUp' })
  const {
    isLoading,
    data: [signedUp],
  } = useAsyncData([(state) => state.signUp.signedUp])

  const defaultValues = useMemo(() => ({ firstName: '', lastName: '', email: '' }), [])
  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: async ({ email, firstName, lastName }) => {
      await dispatch(
        storeActions.user.signUp({
          email,
          firstName,
          lastName,
        }),
      )
    },
    schema: {
      email: emailSchema,
      firstName: userSchema.firstName,
      lastName: userSchema.lastName,
    },
  })

  if (signedUp) {
    return (
      <div className="relative inset-0 z-10 m-auto flex h-full flex-1 flex-col items-center justify-center gap-2">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>{t('thanksTitle')}</CardTitle>
            <CardDescription>{t('thanksDescription')}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }
  return (
    <div className="relative inset-0 z-10 m-auto flex h-full flex-1 flex-col items-center justify-center gap-2">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription>
            <Trans
              i18nKey="signUp.description"
              components={[<Label className="text-primary">CaseAI</Label>]}
            />
          </CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={submit} onKeyDown={submitOnPressMetaEnter}>
            <CardContent className="grid gap-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{tc('email')}</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="m@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{tc('firstName')}</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="Jane" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{tc('lastName')}</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
            <CardFooter>
              <Button className="w-full" type="submit" disabled={!isFormValid || isLoading}>
                <div>
                  {isFormValid ? (
                    <ShieldCheck className="size-5" />
                  ) : (
                    <Circle className="size-5 opacity-50" />
                  )}
                </div>
                {isLoading ? t('signingUp') : t('cta')}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </div>
  )
}
